import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, KeyRound, Send } from "lucide-react";
import { AuthCardLayout } from "@/components/layout/AuthCardLayout";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { requestPasswordReset } from "@/lib/api/auth";
import { useDialog } from "@/lib/dialogs/DialogProvider";
import { useAsyncAction } from "@/lib/hooks/useAsyncAction";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const { alert } = useDialog();
  const { run, pending } = useAsyncAction();
  const navigate = useNavigate();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    run(async () => {
      const result = await requestPasswordReset(email.trim());
      await alert(result.message, { title: "Passwort zurücksetzen" });
      navigate("/login");
    });
  };

  return (
    <AuthCardLayout>
      <h3 className="mb-2 flex items-center justify-center gap-2 text-lg font-bold text-primary">
        <KeyRound className="size-5" aria-hidden="true" /> Passwort vergessen
      </h3>
      <p className="mb-6 text-sm leading-[1.4] text-muted-foreground">
        Gib deine E-Mail-Adresse ein. Wir senden dir einen Link zum Zurücksetzen deines Passworts.
      </p>

      <form onSubmit={handleSubmit}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="forgot-email">E-Mail-Adresse</FieldLabel>
            <Input
              id="forgot-email"
              type="email"
              placeholder="z. B. name@cleverfleet.de"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Button type="submit" className="w-full hover:bg-primary-dark" disabled={pending}>
            Zurücksetzen <Send data-icon className="size-4" aria-hidden="true" />
          </Button>
        </FieldGroup>
      </form>

      <div className="mt-6 border-t border-dashed border-border pt-4">
        <Button type="button" variant="outline" className="w-full text-primary" onClick={() => navigate("/login")}>
          <ArrowLeft className="size-3.5" aria-hidden="true" /> Zurück zum Login
        </Button>
      </div>
    </AuthCardLayout>
  );
}
